import { defaultButtonIconList } from "@jugl-web/domain-resources/chat-bot/utils/defaultButtonIcons";
import { useFileSelect } from "@jugl-web/domain-resources/files/hooks/useFileSelect";
import {
  InteractiveContainer,
  Menu,
  PlainButton,
} from "@jugl-web/ui-components";
import { CropImageModal } from "@jugl-web/ui-components/web/AvatarSelect/components/CropImageModal/CropImageModal";
import {
  cx,
  isFileTypeAccepted,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as BinIcon } from "./assets/bin.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as UploadIcon } from "./assets/upload.svg";

interface ChatBotIconPickerProps {
  /** ID of the default icon or URL of the custom icon */
  icon: string | File;
  onIconChange: (icon: string | File | null) => void;
}
export const ChatBotIconPicker: FC<ChatBotIconPickerProps> = ({
  icon,
  onIconChange,
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const [pendingFile, setPendingFile] = useState<File | undefined>(undefined);

  const [previousCustomIcon, setPreviousCustomIcon] = useState<
    string | File | null
  >(null);

  const urlRef = useRef<string | null>(null);
  const previousUrlRef = useRef<string | null>(null);

  const { selectFile } = useFileSelect();
  const { toast } = useToast();
  const { t } = useTranslations();

  const customIconUrl = useMemo(() => {
    if (icon instanceof File) {
      const url = URL.createObjectURL(icon);
      urlRef.current = url;
      return url;
    }

    if (typeof icon === "string" && !icon.startsWith("default")) {
      return icon;
    }

    return null;
  }, [icon]);

  const isCustomIconSelected = !!customIconUrl;

  const previousCustomIconUrl = useMemo(() => {
    if (previousCustomIcon instanceof File) {
      const url = URL.createObjectURL(previousCustomIcon);
      previousUrlRef.current = url;
      return url;
    }

    if (
      typeof previousCustomIcon === "string" &&
      !previousCustomIcon.startsWith("default")
    ) {
      return previousCustomIcon;
    }

    return null;
  }, [previousCustomIcon]);

  const hasPreviousCustomIcon =
    !!previousCustomIconUrl && !isCustomIconSelected;

  const handleFileChange = useCallback(async () => {
    const selectedFiles = await selectFile({
      acceptTypes: "image/*",
      maxFileSize: 307200,
      multiple: false,
    });

    const file = selectedFiles?.[0];

    if (file) {
      if (!isFileTypeAccepted(file, "image/*")) {
        toast(
          t({
            id: "cx-ai-agents-page.incorrect-file-format",
            defaultMessage: "Incorrect file format ❌",
          }),
          { variant: "error" }
        );
        return;
      }

      setPendingFile(file);
      setIsCropModalOpen(true);
    }
  }, [selectFile, t, toast]);

  const handleDefaultIconSelect = useCallback(
    (iconId: string) => {
      if (isCustomIconSelected) {
        setPreviousCustomIcon(icon);
      }

      onIconChange(iconId);
    },
    [icon, isCustomIconSelected, onIconChange]
  );

  useEffect(
    () => () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }

      if (previousUrlRef.current) {
        URL.revokeObjectURL(previousUrlRef.current);
      }
    },
    []
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="group relative">
          <InteractiveContainer
            className={cx(
              "flex h-[46px] w-[46px] items-center justify-center rounded-xl border border-solid border-grey-400 transition-colors",
              isCustomIconSelected && "border-dark-700"
            )}
            onClick={
              hasPreviousCustomIcon
                ? () => onIconChange(previousCustomIcon)
                : handleFileChange
            }
            isDisabled={isCustomIconSelected}
          >
            {customIconUrl || previousCustomIconUrl ? (
              <div className="h-5 w-5 overflow-hidden rounded-full">
                <img
                  src={(customIconUrl ?? previousCustomIconUrl) as string}
                  className="h-full w-full object-cover"
                  alt=""
                />
              </div>
            ) : (
              <UploadIcon />
            )}
          </InteractiveContainer>
          {isCustomIconSelected && (
            <Menu
              placement="top"
              renderTrigger={({ Trigger, triggerRef }) => (
                <Trigger
                  ref={triggerRef}
                  as={PlainButton}
                  className="absolute -top-2.5 -right-2.5 flex h-8 w-8 items-center justify-center rounded-full border border-solid border-[#F2F2F2] bg-white opacity-0 transition-opacity group-hover:opacity-100 hover:opacity-100"
                >
                  <MoreIcon />
                </Trigger>
              )}
              autoClose
              sections={[
                [
                  {
                    id: "select",
                    label: t({
                      id: "cx-ai-agents-page.select-new-avatar",
                      defaultMessage: "Select new",
                    }),
                    icon: <UploadIcon />,
                    onSelect: () => handleFileChange(),
                  },
                  {
                    id: "remove",
                    label: t({
                      id: "cx-ai-agents-page.remove-pic",
                      defaultMessage: "Remove Pic",
                    }),
                    icon: <BinIcon />,
                    onSelect: () => {
                      setPreviousCustomIcon(null);
                      onIconChange(null);
                    },
                  },
                ],
              ]}
            />
          )}
        </div>
        <div className="h-[46px] w-px shrink-0 rounded-full bg-dark-100" />
        {defaultButtonIconList.map((iconData, index) => {
          const isSelected =
            typeof icon === "string" && icon.startsWith(iconData.id);

          return (
            <InteractiveContainer
              className={cx(
                "flex h-[46px] w-[46px] items-center justify-center rounded-xl border border-solid border-grey-400 transition-colors",
                isSelected && "border-dark-700"
              )}
              key={+index}
              onClick={() => handleDefaultIconSelect(iconData.id)}
            >
              <iconData.Icon className="h-5 w-5 text-dark-500" />
            </InteractiveContainer>
          );
        })}
      </div>
      <CropImageModal
        isOpen={isCropModalOpen}
        image={pendingFile}
        onClose={() => setIsCropModalOpen(false)}
        onChangeImage={handleFileChange}
        onSave={(croppedFile) => {
          onIconChange(croppedFile);
          setIsCropModalOpen(false);
        }}
      />
    </>
  );
};
